/* corex-textil-editor-align.js · Reglas puras de alineación del personalizador de camisetas (sin DOM).
   Dos niveles, como en Illustrator:
   - Impresión en la prenda (caja real del diseño, cm de prenda en la talla de referencia): izquierda/derecha = el
     BORDE del diseño hace tope con el límite útil del costado; centrar en el eje; logo de pecho izquierdo/derecho
     (centro a 12 cm, con el mismo tope); arriba y centrar en vertical; en mangas, centrar y abajo (moviendo las
     capas en su 10×10).
   - Capas dentro de una impresión (cm relativos al centro de la impresión, x a la derecha, y hacia abajo):
     alinear bordes y centros y distribuir el espacio.
   - Tope al mover: desplazamiento mínimo para que una caja quede dentro de unos límites. */
(function (global) {
  'use strict';

  /* Logo de pecho izquierdo/derecho: centro del diseño a esta distancia del eje de la prenda, en la talla de referencia
     (si el diseño es grande, se acerca al eje hasta hacer tope). Es un desplazamiento horizontal más, así que en las
     demás tallas escala un 3,5 % por talla como el resto. */
  var POS_PECHO_CM = 12;
  /* "Arriba": borde superior del diseño a la distancia habitual del cuello (la misma que el placeholder del campo). */
  var TOP_DIST_CM = 8;
  /* Zona útil vertical de pecho/espalda para "Centrar vertical": de TOP_DIST_CM al bajo menos este margen. */
  var V_CENTER_MARGIN_CM = 8;
  var EPS = 1e-9;

  function r2(v) { return Math.round(v * 100) / 100; }

  /* Centro horizontal más lejano del eje (cm) al que puede ir un diseño de ancho w sin pasar del límite útil del
     costado (halfW, desde el eje). Sin halfW no hay tope; si el diseño no cabe, al eje. */
  function maxCenter(w, halfW) {
    if (halfW == null) return Infinity;
    return Math.max(0, halfW - w / 2);
  }
  /* Posición nueva del diseño de una impresión de delante/espalda.
     rect: { w, h, top, cu } del diseño; o: { hemV (largo cuello→bajo en la talla de referencia), halfW (mitad del
     ancho útil del pecho: ½ pecho − CHEST_MARGIN_CM/2, en la talla de referencia) }.
     borde-izq/borde-der: el borde del diseño contra el límite útil de ese costado (izquierda/derecha de la imagen).
     Pecho izquierdo de quien la lleva puesta = derecha de la foto frontal (cu > 0). Devuelve { top, cu }. */
  function printTarget(op, rect, o) {
    if (o && o.zone) return zoneTarget(op, rect, o.zone, o.topDist);
    var out = { top: rect.top, cu: rect.cu }, halfW = o && o.halfW != null ? o.halfW : null;
    var reach = maxCenter(rect.w, halfW);
    if (op === 'centro-h') out.cu = 0;
    else if (op === 'borde-izq' || op === 'borde-der') {
      if (halfW == null) throw new Error('Falta el ancho útil para alinear al costado.');
      out.cu = op === 'borde-der' ? reach : -reach;
    }
    else if (op === 'pecho-izq') out.cu = Math.min(POS_PECHO_CM, reach);
    else if (op === 'pecho-der') out.cu = -Math.min(POS_PECHO_CM, reach);
    else if (op === 'arriba') out.top = TOP_DIST_CM;
    else if (op === 'centro-v') {
      var bottom = (o && o.hemV ? o.hemV : 0) - V_CENTER_MARGIN_CM;
      out.top = Math.max(TOP_DIST_CM, (TOP_DIST_CM + bottom) / 2 - rect.h / 2);
    } else throw new Error('Alineación desconocida: ' + op);
    return { top: r2(out.top), cu: r2(out.cu) || 0 };
  }

  /* Sudaderas: alinear dentro de la zona útil z { x0, x1, y0, y1 } (pecho de la cremallera, bajo la cremallera de 1/4, sobre el
     bolsillo): el centro y los bordes son los de la zona, y «arriba» no sube de su límite superior. topDist: distancia
     habitual al cuello de esa zona (detrás de la sudadera con capucha, 15 cm); por defecto TOP_DIST_CM. */
  function zoneTarget(op, rect, z, topDist) {
    var out = { top: rect.top, cu: rect.cu }, half = rect.w / 2, top0 = Math.max(topDist > 0 ? topDist : TOP_DIST_CM, z.y0);
    if (op === 'centro-h') out.cu = (z.x0 + z.x1) / 2;
    else if (op === 'borde-izq') out.cu = Math.min(z.x0 + half, (z.x0 + z.x1) / 2);
    else if (op === 'borde-der') out.cu = Math.max(z.x1 - half, (z.x0 + z.x1) / 2);
    else if (op === 'pecho-izq') out.cu = Math.min(POS_PECHO_CM, z.x1 - half);
    else if (op === 'pecho-der') out.cu = -Math.min(POS_PECHO_CM, -z.x0 - half);
    else if (op === 'arriba') out.top = top0;
    else if (op === 'centro-v') out.top = Math.max(top0, (z.y0 + z.y1) / 2 - rect.h / 2);
    else throw new Error('Alineación desconocida: ' + op);
    return { top: r2(out.top), cu: r2(out.cu) || 0 };
  }
  /* Tope al mover: { dx, dy } mínimo para que la caja b { x0, x1, y0, y1 } quede dentro de lim { x0, x1, y0, y1 }.
     Si la caja es más ancha que el límite, se centra en él; si es más alta, se apoya arriba (nunca más cerca del cuello
     que el mínimo; lo que sobra queda abajo, y el aviso de que no cabe lo da el máximo de la talla). */
  function clampShift(b, lim) {
    var axis = function (a0, a1, l0, l1, top) {
      if (a1 - a0 > l1 - l0) return top ? l0 - a0 : (l0 + l1) / 2 - (a0 + a1) / 2;
      if (a0 < l0) return l0 - a0;
      if (a1 > l1) return l1 - a1;
      return 0;
    };
    var d = { dx: axis(b.x0, b.x1, lim.x0, lim.x1), dy: axis(b.y0, b.y1, lim.y0, lim.y1, true) };
    return lim.halfAt ? clampNarrowTop(b, lim, d) : d;
  }
  /* Prendas que se estrechan arriba (raglán, tirantes): lim.halfAt(v) = medio ancho útil a la altura v, que crece hacia
     abajo. El borde superior del diseño es el más estrecho: si no cabe, el diseño baja hasta que quepa (sin pasar del
     bajo) y luego se mete en horizontal. */
  function clampNarrowTop(b, lim, d) {
    var w2 = (b.x1 - b.x0) / 2, y0 = b.y0 + d.dy, yMax = lim.y1 - (b.y1 - b.y0);
    while (y0 < yMax && lim.halfAt(y0) < w2) y0 = Math.min(yMax, y0 + NARROW_STEP_CM);
    var hw = Math.max(0, lim.halfAt(y0)), cx = (b.x0 + b.x1) / 2 + d.dx;
    if (w2 >= hw) cx = 0;
    else if (cx - w2 < -hw) cx = -hw + w2;
    else if (cx + w2 > hw) cx = hw - w2;
    return { dx: cx - (b.x0 + b.x1) / 2, dy: y0 - b.y0 };
  }
  var NARROW_STEP_CM = 0.1;

  /* Manga (impresión fija de 10×10): desplazamiento de todas las capas.
     b: caja del diseño relativa al centro de la impresión { w, h, cx, cy }; area: { w, h }. */
  function sleeveShift(op, b, area) {
    if (op === 'manga-centro') return { dx: r2(-b.cx), dy: 0 };
    if (op === 'manga-abajo') return { dx: 0, dy: r2(area.h / 2 - (b.cy + b.h / 2)) };
    throw new Error('Alineación desconocida: ' + op);
  }

  /* Caja { x0, x1, y0, y1 } de unos puntos { x, y } (las esquinas giradas de una capa). */
  function boxOf(points) {
    var xs = points.map(function (p) { return p.x; }), ys = points.map(function (p) { return p.y; });
    return { x0: Math.min.apply(null, xs), x1: Math.max.apply(null, xs), y0: Math.min.apply(null, ys), y1: Math.max.apply(null, ys) };
  }
  function union(boxes) {
    return {
      x0: Math.min.apply(null, boxes.map(function (b) { return b.x0; })), x1: Math.max.apply(null, boxes.map(function (b) { return b.x1; })),
      y0: Math.min.apply(null, boxes.map(function (b) { return b.y0; })), y1: Math.max.apply(null, boxes.map(function (b) { return b.y1; }))
    };
  }

  /* Alinear capas. boxes: [{ id, x0, x1, y0, y1 }] de las capas a mover; ref: caja de referencia (la selección
     si hay 2 o más; el diseño completo si hay una). Devuelve { id: { dx, dy } }. */
  function alignLayers(op, boxes, ref) {
    return boxes.reduce(function (acc, b) {
      var d = { dx: 0, dy: 0 };
      if (op === 'izq') d.dx = ref.x0 - b.x0;
      else if (op === 'der') d.dx = ref.x1 - b.x1;
      else if (op === 'centro-h') d.dx = (ref.x0 + ref.x1) / 2 - (b.x0 + b.x1) / 2;
      else if (op === 'arriba') d.dy = ref.y0 - b.y0;
      else if (op === 'abajo') d.dy = ref.y1 - b.y1;
      else if (op === 'centro-v') d.dy = (ref.y0 + ref.y1) / 2 - (b.y0 + b.y1) / 2;
      else throw new Error('Alineación desconocida: ' + op);
      acc[b.id] = { dx: r2(d.dx), dy: r2(d.dy) };
      return acc;
    }, {});
  }

  /* Distribuir el espacio (3 o más capas): las de los extremos no se mueven y el hueco entre cajas consecutivas
     queda igual. axis 'h' (por x) o 'v' (por y). Devuelve { id: { dx, dy } }. */
  function distribute(axis, boxes) {
    var a0 = axis === 'h' ? 'x0' : 'y0', a1 = axis === 'h' ? 'x1' : 'y1', key = axis === 'h' ? 'dx' : 'dy';
    var out = {};
    if (boxes.length < 3) return out;
    var sorted = boxes.slice().sort(function (p, q) { return p[a0] - q[a0] || p[a1] - q[a1]; });
    var span = sorted[sorted.length - 1][a1] - sorted[0][a0];
    var sizes = sorted.reduce(function (s, b) { return s + (b[a1] - b[a0]); }, 0);
    var gap = (span - sizes) / (sorted.length - 1), pos = sorted[0][a0];
    sorted.forEach(function (b) {
      var d = { dx: 0, dy: 0 };
      d[key] = r2(Math.abs(pos - b[a0]) < EPS ? 0 : pos - b[a0]);
      out[b.id] = d;
      pos += (b[a1] - b[a0]) + gap;
    });
    return out;
  }

  var api = {
    POS_PECHO_CM: POS_PECHO_CM, TOP_DIST_CM: TOP_DIST_CM, V_CENTER_MARGIN_CM: V_CENTER_MARGIN_CM,
    printTarget: printTarget, maxCenter: maxCenter, clampShift: clampShift, sleeveShift: sleeveShift, boxOf: boxOf, union: union, alignLayers: alignLayers, distribute: distribute
  };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else global.CorexTextilAlign = api;
})(this);
